import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';

import { getWAFRuleSetsTableColumns } from '@constants';
import { FormListLayout } from '@containers';
import { ApiEndpoint, ApiType, FormActionType, FormType, Translation, VariantType, WafSchemaType } from '@enums';
import {
  useAppDispatch,
  useFetchFormList,
  useFormError,
  useFormFilter,
  useFormHandlers,
  useFormSelectedList,
} from '@hooks';
import { clearCheckBox, resetFormFilter, submitFormRequest, updateFormProcedure } from '@store';
import i18n from '@translations';
import type { WAFRuleSetsProps } from '@types';

import { WAFFilterModal } from '../../subComponents';

export const WAFRuleSets: FC<WAFRuleSetsProps> = ({ options = [] }) => {
  const dispatch = useAppDispatch();
  const { key } = useLocation();
  const { id: application_id } = useParams();

  const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);

  const formType = FormType.CdnWAF;
  const endpoint = ApiEndpoint.RULE;

  const { t: tTables } = useTranslation(Translation.Description, { keyPrefix: 'configuration.tables' });
  const { t: tForms } = useTranslation(Translation.Configuration, { keyPrefix: 'forms.waf' });

  const filter = useFormFilter(formType);
  const filterCriteria = filter?.filterCriteria ?? [];

  const selectedIds = useFormSelectedList({ formType, endpoint });
  const submitError = useFormError({ formType, endpoint });

  const filterParams = useMemo(() => {
    return filterCriteria?.reduce(
      (acc, cur) => {
        if (cur.value !== 'all') {
          acc[cur.label] = cur.value;
        }
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [filterCriteria]);

  useFetchFormList({
    formType,
    params: { application_id, ...filterParams },
    globalOptions: {
      filter: true,
      pagination: true,
      endpoint: ApiEndpoint.RULE,
    },
    hookOptions: {
      resetFormTrigger: false,
      custom: {
        condition: !submitError,
        dependencies: [key],
      },
    },
  });

  const handleBatchSubmit = (selectedStatus: string) => {
    const batchSubmitList = selectedIds.map((id: string) => ({
      id: id,
      rule_status: selectedStatus,
    }));

    dispatch(updateFormProcedure({ formType, procedure: FormActionType.Update }));

    dispatch(
      submitFormRequest({
        formType,
        endpoint,
        params: { application_id },
        formData: batchSubmitList,
        globalOptions: {
          endpoint,
          refetch_endpoint: ApiEndpoint.RULE,
          refetch_id: application_id,
          returnResult: true,
          apiType: ApiType.PUT,
        },
      }),
    );

    dispatch(resetFormFilter({ formType }));

    dispatch(
      clearCheckBox({
        formType,
        endpoint,
      }),
    );
  };

  const wafRuleSetsTableColumns =
    useMemo(() => getWAFRuleSetsTableColumns({ options: options[WafSchemaType.RuleStatus] }), [tTables]) ?? [];

  const wafRuleSetsFormHandlers = useFormHandlers({
    formType,
    handlerOptions: [
      {
        type: FormActionType.Filter,
        function: () => setOpenFilterModal(true),
        label: i18n.t(`${Translation.Configuration}:buttons.waf.filter`),
        buttonType: VariantType.Outlined,
      },
      {
        type: FormActionType.Batch,
        function: handleBatchSubmit,
        label: i18n.t(`${Translation.Configuration}:forms.waf.ruleSets.action`),
      },
    ],
  });

  const handleFilterModalClose = () => {
    setOpenFilterModal(false);
  };

  return (
    <FormListLayout
      label={{
        title: tForms('ruleSets.wafSecurityRuleGroups'),
        subTitle: tForms(
          'ruleSets.manageAndConfigureRuleGroupsToProtectYourWebApplicationsFromCommonVulnerabilitiesAndAttacks',
        ),
      }}
      tableData={{
        columns: wafRuleSetsTableColumns,
        actionHandlers: wafRuleSetsFormHandlers,
        hasChildren: true,
        formType,
        endpoint,
      }}
    >
      {openFilterModal && <WAFFilterModal open={openFilterModal} onClose={handleFilterModalClose} options={options} />}
    </FormListLayout>
  );
};
